import * as THREE from 'three';
import type { BirdHaunt, Point } from '../../../shared/game/world-types.js';
import { BirdModel } from './bird-model.js';
import { BIRDS } from './bird-species.js';
import type { CritterGround } from './critter-ground.js';
import { between, type Random } from './critter-math.js';
import { releaseCritter } from './critter-parts.js';
import { PathFlight } from './path-flight.js';

/** How far a party at `at` is from the nearest part of a haunt's airspace, in metres. */
export function hauntDistance(haunt: BirdHaunt, ground: CritterGround, at: Point) {
  if ('wheel' in haunt) return Math.max(0, Math.hypot(at.x - haunt.wheel.x, at.z - haunt.wheel.z) - haunt.wheel.radius - haunt.wheel.spread * haunt.count);
  const { z, reach, offshore } = haunt.beat, nearest = THREE.MathUtils.clamp(at.z, z - reach, z + reach);
  return Math.max(0, Math.hypot(at.x - (ground.coastEdge(nearest) - offshore), at.z - nearest));
}

/**
 * A flock that is always in the air over one place: wheeling around a lantern or a graveyard, or beating up and down the
 * shore. Each bird's position is a function of game time, so a paused game holds the flock still and two frames never disagree.
 */
export class HauntFlock {
  private readonly birds: { model: BirdModel; flight: PathFlight; phase: number; position: THREE.Vector3; ahead: THREE.Vector3; later: THREE.Vector3 }[];

  constructor(readonly haunt: BirdHaunt, private ground: CritterGround, parent: THREE.Object3D, random: Random) {
    const species = BIRDS[haunt.kind];
    this.birds = Array.from({ length: haunt.count }, () => {
      const model = new BirdModel(species, random() * 40); model.setOverlay(true);
      model.root.scale.setScalar(species.scale * between(random, .9, 1.1)); model.root.visible = false; parent.add(model.root);
      return { model, flight: new PathFlight(species, random), phase: random() * Math.PI * 2, position: new THREE.Vector3(), ahead: new THREE.Vector3(), later: new THREE.Vector3() };
    });
  }

  update(time: number, dt: number) {
    for (const [index, bird] of this.birds.entries()) {
      this.place(index, bird.phase, time, bird.position); this.place(index, bird.phase, time + .25, bird.ahead); this.place(index, bird.phase, time + .6, bird.later);
      bird.flight.follow(dt, bird.position, bird.ahead, bird.later);
      bird.model.apply(bird.flight.action);
      bird.model.root.position.copy(bird.position); bird.model.root.rotation.y = bird.flight.heading; bird.model.root.visible = true;
    }
  }

  hide() { for (const { model } of this.birds) model.root.visible = false; }
  dispose() { for (const { model } of this.birds) releaseCritter(model.root); }

  private place(index: number, phase: number, time: number, into: THREE.Vector3) {
    const { haunt } = this;
    if ('wheel' in haunt) {
      const { x, z, radius, spread, squash = .85 } = haunt.wheel, a = time * haunt.rate * (1 + (index % 4) * .07) + phase, r = radius + index * spread;
      return into.set(x + Math.cos(a) * r, haunt.height + Math.sin(a * 2.1) * 2.4, z + Math.sin(a) * r * squash);
    }
    // Along the shore: slowly up and down the coast, drifting in and out over the surf and rising and falling with each pass.
    const { z, reach, offshore, swing } = haunt.beat, a = time * haunt.rate + phase, along = z + Math.sin(a) * reach;
    return into.set(this.ground.coastEdge(along) - offshore - Math.cos(a * 1.7) * swing - (index % 3) * 5, haunt.height + (index % 4) * 2 + Math.sin(a * 2) * 1.5, along);
  }
}
